import { keepPreviousData } from "@tanstack/react-query"
import { useDebounce as useDebouncedValue } from "hooks/use-debounced-value"
import { useSearchForms } from "sdk/command-palette/queries"
import { SUBPAGES } from "../pages"

export const PAGE_ID = "form-search"

export const SHORTCUT = "f f"

const MIN_QUERY_LENGTH = SUBPAGES[PAGE_ID].minQueryLength

const DEBOUNCE_MS = 300

const SEARCH_ENTRY_COMMAND = {
  id: "search-forms",
  label: "Search Landing pages & forms…",
  icon: "search",
  group: "Search",
  keywords: ["find", "form", "landing page", "page", "signup"],
  shortcut: SHORTCUT,
  onSelect: api => api.pushPage(PAGE_ID)
}

const GROUP = "Landing Pages & Forms"

const ICONS = {
  form: "form",
  landing_page: "landing-page"
}

const toFormCommand = form => ({
  id: `form-${form.id}`,
  label: form.name,
  icon: ICONS[form.type] ?? ICONS.form,
  group: GROUP,
  href: form.url,
  onSelect: api => api.navigate(form.url)
})

/**
 * Provider for landing page & form search. At the root it offers the "Search
 * Landing pages & forms…" entry that opens the subpage; on that subpage it
 * returns the async, server-filtered results (selecting one opens its editor).
 *
 * @type {import("../types").Provider}
 */
export function useFormSearchProvider({ query, activePageId }) {
  const active = activePageId === PAGE_ID
  const trimmedQuery = query.trim()
  const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS)

  const { data, isFetching } = useSearchForms({
    params: { query: debouncedQuery },
    enabled:
      active &&
      debouncedQuery === trimmedQuery &&
      debouncedQuery.length >= MIN_QUERY_LENGTH,
    placeholderData: keepPreviousData
  })

  if (!activePageId) {
    return { commands: [SEARCH_ENTRY_COMMAND], isLoading: false }
  }

  if (!active) return { commands: [], isLoading: false }

  return {
    commands: (data?.forms ?? []).map(toFormCommand),
    isLoading: isFetching
  }
}

/**
 * @returns {import("../types").Shortcut[]}
 */
export function getFormSearchShortcuts() {
  return [{ shortcut: SHORTCUT, onTrigger: api => api.pushPage(PAGE_ID) }]
}
